import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, AppWindow, ExternalLink, Server, Globe, ShieldCheck, ShieldAlert, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { YamlSourceBadge } from '@/components/YamlSourceBadge';

export default function AppsPage() {
  const { authAxios } = useAuth();
  const [apps, setApps] = useState([]);
  const [intel, setIntel] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const api = authAxios();
      const [appsRes, endpointsRes, intelRes] = await Promise.all([
        api.get('/apps'),
        api.get('/endpoints'),
        api.get('/domain-intel/latest')
      ]);
      
      const rawApps = Array.isArray(appsRes.data) ? appsRes.data : [];
      const rawEndpoints = Array.isArray(endpointsRes.data) ? endpointsRes.data : [];
      
      const appsData = rawApps.map(app => ({
        ...app,
        endpoints: rawEndpoints.filter(ep => ep.app_id === app.id)
      }));
      
      setApps(appsData);
      setIntel(Array.isArray(intelRes.data) ? intelRes.data : []);
    } catch (err) {
      console.error('Apps fetch error:', err);
      toast.error('Failed to fetch apps');
      setApps([]);
      setIntel([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const getEndpointStatus = (fqdn, intent) => {
    const info = Array.isArray(intel) ? intel.find(i => i.fqdn === fqdn) : null;
    if (!info) return null;
    
    // Check miswired
    let isMiswired = false;
    // Simple intent check if we had intent logic in frontend or returned from backend
    // For now, rely on backend status
    
    return info;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="w-8 h-8 text-[#EF694C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-white tracking-tight">
            Fyre Apps
          </h1>
          <YamlSourceBadge className="text-xs" />
        </div>
        <p className="text-sm text-[#a1a1aa] mt-1">
          {apps.length} applications registered
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {apps.map((app) => (
          <Card key={app.id} className="bg-[#121214] border-white/10 hover:border-[#EF694C]/50 transition-all duration-300">
            <CardContent className="p-6 space-y-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#EF694C]/10 flex items-center justify-center text-[#EF694C]">
                    <AppWindow className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">{app.name}</h3>
                    <Badge variant="outline" className="text-[10px] border-white/10 text-[#a1a1aa]">
                      {app.category}
                    </Badge>
                  </div>
                </div>
                <Badge className={
                  app.status === 'live' ? 'bg-emerald-500/10 text-emerald-500' :
                  app.status === 'dev' ? 'bg-blue-500/10 text-blue-500' :
                  app.status === 'planned' ? 'bg-gray-500/10 text-gray-500' :
                  'bg-amber-500/10 text-amber-500'
                }>
                  {app.status}
                </Badge>
              </div>

              <p className="text-sm text-[#a1a1aa] line-clamp-2 min-h-[40px]">
                {app.description}
              </p>

              {/* Hosting Info */}
              {app.hosting && (app.hosting.asset_hint || app.hosting.stack_hint) && (
                <div className="p-3 rounded-lg bg-white/5 space-y-2">
                  {app.hosting.asset_hint && (
                    <div className="flex items-center gap-2 text-xs text-[#a1a1aa]">
                      <Server className="w-3 h-3" />
                      <span>{app.hosting.asset_hint}</span>
                    </div>
                  )}
                  {app.hosting.stack_hint && (
                    <div className="flex items-center gap-2 text-xs text-[#a1a1aa]">
                      <span className="font-mono bg-black/20 px-1 rounded">{app.hosting.stack_hint}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Endpoints */}
              {app.endpoints && app.endpoints.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-white/5">
                  <p className="text-xs font-semibold text-[#52525b] uppercase">Endpoints</p>
                  {app.endpoints.map(ep => {
                    const info = getEndpointStatus(ep.fqdn);
                    return (
                      <div key={ep.fqdn} className="flex flex-col gap-1 text-xs">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-[#a1a1aa]">
                            <Globe className="w-3 h-3" />
                            <span className="font-mono">{ep.fqdn}</span>
                          </div>
                          {info && (
                            <Badge className={`text-[10px] h-4 ${
                              info.status === 'ok' ? 'bg-emerald-500/10 text-emerald-500' :
                              info.status === 'external' ? 'bg-blue-500/10 text-blue-500' :
                              info.status === 'miswired' ? 'bg-amber-500/10 text-amber-500' :
                              info.status === 'parked' ? 'bg-purple-500/10 text-purple-500' :
                              'bg-gray-500/10 text-gray-500'
                            }`}>
                              {info.status}
                            </Badge>
                          )}
                        </div>
                        {info?.tls?.days_remaining !== undefined && (
                          <div className={`flex items-center gap-1 ml-5 ${
                            info.status === 'external' ? 'text-blue-400' :
                            info.tls.days_remaining < 30 ? 'text-red-500' : 'text-emerald-500'
                          }`}>
                            {info.tls.days_remaining < 30 ? <ShieldAlert className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                            <span>TLS: {info.tls.days_remaining}d</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Links */}
              {app.links && app.links.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {app.links.map((link, i) => (
                    <a
                      key={i}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-[#EF694C] hover:text-[#d65d43] transition-colors"
                    >
                      {link.label}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
